import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Building2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useWorkspaces } from '../../hooks/useWorkspaces';
import { useChatStore } from '../../store/chatStore';
import { addToast } from '../../store/notificationStore';
import { LoadingIndicator } from '../feedback/LoadingIndicator';
import { cn } from '../../utils/cn';
import type { Workspace } from '../../types';

export function WorkspaceSelector() {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  const { data: workspaces = [], isLoading, isError, refetch } = useWorkspaces();
  const { selectedWorkspace, setSelectedWorkspace, setCurrentSession } = useChatStore();

  // Auto-select first workspace if none is selected (e.g. after fresh login)
  useEffect(() => {
    if (!selectedWorkspace && workspaces.length > 0) {
      setSelectedWorkspace(workspaces[0]);
    }
  }, [workspaces, selectedWorkspace, setSelectedWorkspace]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleWorkspaceSelect(workspace: Workspace) {
    setSelectedWorkspace(workspace);
    setCurrentSession(null);
    queryClient.invalidateQueries({ queryKey: ['sessions'] });
    setIsOpen(false);
  }

  if (isLoading) {
    return <LoadingIndicator variant="spinner" size="sm" />;
  }

  if (isError) {
    return (
      <div className="flex items-center gap-2 text-sm text-red-600">
        <Building2 className="h-4 w-4" />
        <span>Failed to load workspaces</span>
        <button
          type="button"
          onClick={() => {
            addToast('info', 'Retrying workspace load...');
            refetch();
          }}
          className="ml-1 font-medium text-primary-600 hover:text-primary-700 underline focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
        >
          Retry
        </button>
      </div>
    );
  }

  if (workspaces.length === 0) {
    return (
      <div className="flex items-center gap-2 text-sm text-neutral-500">
        <Building2 className="h-4 w-4" />
        <span>No workspaces configured</span>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => {
          const opening = !isOpen;
          setIsOpen(opening);
          if (opening) refetch();
        }}
        className={cn(
          'flex items-center gap-2 px-3 py-2 rounded-lg',
          'bg-white border border-neutral-200 hover:border-neutral-300',
          'text-sm font-medium text-neutral-700',
          'transition-colors duration-150',
          'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2'
        )}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <Building2 className="h-4 w-4 text-neutral-500" />
        <span className="truncate max-w-[160px]">
          {selectedWorkspace?.name || 'Select Workspace'}
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-neutral-500 transition-transform duration-150',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {isOpen && (
        <ul
          role="listbox"
          className={cn(
            'absolute left-0 top-full mt-1 z-50',
            'w-72 max-h-64 overflow-y-auto',
            'bg-white border border-neutral-200 rounded-lg shadow-lg',
            'py-1'
          )}
        >
          {workspaces.map((workspace) => (
            <li
              key={workspace.id}
              role="option"
              aria-selected={selectedWorkspace?.id === workspace.id}
              onClick={() => handleWorkspaceSelect(workspace)}
              className={cn(
                'flex flex-col gap-0.5 px-3 py-2 cursor-pointer',
                'hover:bg-neutral-50 transition-colors duration-100',
                selectedWorkspace?.id === workspace.id && 'bg-primary-50 border-l-2 border-primary-500'
              )}
            >
              <span className="text-sm font-medium text-neutral-800">
                {workspace.name}
              </span>
              {workspace.description && (
                <span className="text-xs text-neutral-600 truncate">
                  {workspace.description}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default WorkspaceSelector;
